/**
 * Rekenhulpjes voor Planning → Nog in te plannen. De echte rekenregels
 * (offerte-uren, signalen, ruimte) zitten in de engine; dit is alleen wat het
 * scherm zelf nog filtert en opmaakt — puur, dus los te testen.
 */
const MAANDEN = ['jan', 'feb', 'mrt', 'apr', 'mei', 'jun', 'jul', 'aug', 'sep', 'okt', 'nov', 'dec'];

export const datumKort = (iso) => {
  if (!iso) return '';
  const d = new Date(`${iso}T00:00:00`);
  if (Number.isNaN(d.getTime())) return iso;
  return `${d.getDate()} ${MAANDEN[d.getMonth()]}`;
};

export const uur = (n) => (Number.isFinite(n) ? `${Math.round(n * 10) / 10}`.replace('.', ',') : '–');

/** Welke disciplines horen bij het tabblad? Editors zien montage + feedback, animators animatie. */
export const disciplinesVoorTab = (tab) => {
  if (tab === 'editors') return ['montage', 'feedback'];
  if (tab === 'animators') return ['animatie'];
  return ['montage', 'animatie', 'feedback'];
};

/**
 * Hoort dit project in blok A voor de gekozen disciplines? Zonder offerte-uren
 * weten we niet hoeveel er moet; dan tonen we het project bij "Alles" altijd
 * en bij een tabblad alleen als er taken zonder eigenaar in die discipline zijn.
 */
export const heeftOpenWerk = (rij, disciplines) => {
  if (rij.staat === 'geen_offerte_uren') {
    return disciplines.length === 3
      || disciplines.some((d) => (rij.disciplines?.[d]?.aantalZonderEigenaar || 0) > 0);
  }
  return disciplines.some((d) => (rij.disciplines?.[d]?.open || 0) > 0);
};

// ── weektijdlijn per project ────────────────────────────────────────────────
// Het Planner-To-Do-raster van vier dagen-weken paste niet: taken van maanden
// terug stapelden op de eerste kolom en taken in oktober werden als "geen datum"
// getekend (Joris, 9 sep). De tijdlijn vouwt zich nu om het project heen, per
// week, van de eerste taak tot de laatste deadline.

const DAG_MS = 24 * 60 * 60 * 1000;
export const MAX_WEKEN = 26;

export const maandagVan = (d) => {
  const x = new Date(d); x.setHours(0, 0, 0, 0);
  const dag = (x.getDay() + 6) % 7; // ma = 0
  x.setDate(x.getDate() - dag);
  return x;
};
const isoVan = (d) => `${d.getFullYear()}-${String(d.getMonth() + 1).padStart(2, '0')}-${String(d.getDate()).padStart(2, '0')}`;
const naarDatum = (v) => { if (!v) return null; const d = new Date(`${String(v).slice(0, 10)}T00:00:00`); return Number.isNaN(d.getTime()) ? null : d; };
const weekNummer = (d) => {
  const x = new Date(Date.UTC(d.getFullYear(), d.getMonth(), d.getDate()));
  const dag = x.getUTCDay() || 7; x.setUTCDate(x.getUTCDate() + 4 - dag);
  const jaarStart = new Date(Date.UTC(x.getUTCFullYear(), 0, 1));
  return Math.ceil((((x - jaarStart) / DAG_MS) + 1) / 7);
};

/**
 * De weken die de tijdlijn toont: van de maandag van (vandaag of de vroegste
 * taak, wat eerder is, maar niet meer dan 8 weken terug) tot de vrijdag van de
 * laatste taak/deadline (minstens 4 weken vooruit), gemaximeerd op MAX_WEKEN.
 */
export function weekVenster({ taken = [], deadlines = [], vandaag = new Date() }) {
  const datums = [];
  taken.forEach((t) => { [t.startDate, t.endDate, t.deadline].forEach((v) => { const d = naarDatum(v); if (d) datums.push(d); }); });
  deadlines.forEach((v) => { const d = naarDatum(v); if (d) datums.push(d); });
  const nu = maandagVan(vandaag);
  const vroegste = datums.length ? new Date(Math.min(...datums)) : nu;
  const laatste = datums.length ? new Date(Math.max(...datums)) : nu;
  let start = maandagVan(new Date(Math.min(nu, Math.max(vroegste, nu - 8 * 7 * DAG_MS))));
  let eind = maandagVan(new Date(Math.max(laatste, nu.getTime() + 4 * 7 * DAG_MS)));
  let aantal = Math.round((eind - start) / (7 * DAG_MS)) + 1;
  if (aantal > MAX_WEKEN) { aantal = MAX_WEKEN; eind = new Date(start); eind.setDate(start.getDate() + (MAX_WEKEN - 1) * 7); }
  const weken = [];
  for (let i = 0; i < aantal; i += 1) {
    // Via setDate, niet via milliseconden: over de zomertijdgrens heen schoof
    // de kolom anders een uur — en dus een dag — op.
    const ma = new Date(start); ma.setDate(start.getDate() + i * 7);
    weken.push({ iso: isoVan(ma), maandag: ma, nummer: weekNummer(ma), maand: ma.getMonth(),
      label: `${ma.getDate()} ${['jan', 'feb', 'mrt', 'apr', 'mei', 'jun', 'jul', 'aug', 'sep', 'okt', 'nov', 'dec'][ma.getMonth()]}`,
      huidig: ma.getTime() === nu.getTime(), verleden: ma < nu });
  }
  const vrijdag = new Date(eind); vrijdag.setDate(eind.getDate() + 6);
  return { weken, start, eind: vrijdag, vandaag: nu, afgekapt: datums.length ? new Date(Math.max(...datums)) > vrijdag : false };
}

/** Kolom (weekindex) en breedte in weken voor een taak; null als hij geen datum heeft. */
export function plaatsInVenster(taak, venster) {
  const s = naarDatum(taak.startDate) || naarDatum(taak.endDate);
  if (!s) return null;
  const e = naarDatum(taak.endDate) || s;
  const wStart = Math.round((maandagVan(s) - venster.start) / (7 * DAG_MS));
  const wEind = Math.round((maandagVan(e) - venster.start) / (7 * DAG_MS));
  const n = venster.weken.length;
  if (wEind < 0) return { buiten: 'eerder' };
  if (wStart >= n) return { buiten: 'later' };
  const kolom = Math.max(0, wStart);
  const breedte = Math.max(1, Math.min(n - 1, wEind) - kolom + 1);
  return { kolom, breedte };
}

/** Balken in rijen (lanes) leggen zodat ze elkaar niet overlappen. */
export function legInLanes(items) {
  const lanes = [];
  return [...items].sort((a, b) => a.kolom - b.kolom || b.breedte - a.breedte).map((it) => {
    let lane = lanes.findIndex((vrij) => vrij <= it.kolom);
    if (lane === -1) { lane = lanes.length; lanes.push(0); }
    lanes[lane] = it.kolom + it.breedte;
    return { ...it, lane };
  });
}

/** De samenvatting boven de tijdlijn: wat betekent "deels gepland" hier? */
export function samenvatDiscipline(d = {}) {
  const gepland = Number(d.gepland) || 0;
  const verstreken = Number(d.verstreken) || 0;
  return {
    offerte: Number(d.offerte) || 0,
    toekomst: Math.max(0, gepland - verstreken),
    verstreken,
    zonderDatum: Number(d.toegewezen) || 0,
    zonderEigenaar: Number(d.zonderEigenaar) || 0,
    open: Number(d.open) || 0,
    geteld: Number(d.geteld) || 0,
  };
}

// ── Rust (Joris, 25 sep: "alle schermen best druk") ─────────────────────────
// Hele uren in de lijst; per project één signaal (het belangrijkste), de rest in
// de tooltip; de weektijdlijn als één staafje uren per week per discipline.

/** Hele uren als tekst: 133,5 → "134". */
export const uurHeel = (n) => (Number.isFinite(Number(n)) && n !== null && n !== '' ? String(Math.round(Number(n))) : '–');

const MEER_GEPLAND = { montage: 'montage', animatie: 'animatie', feedback: 'feedback' };

/**
 * Het belangrijkste signaal van een project voor de lijst. `ernst`:
 *  - 'probleem' (rood) — de deadline is niet te halen of al voorbij;
 *  - 'let_op' — er ontbreekt iets wezenlijks (niets gepland, geen deadline, niet afgevinkt);
 *  - 'info' — de rest (deels gepland, meer gepland dan geoffreerd, geen offerte-uren).
 * `overige` bevat de andere signalen, voor de tooltip.
 */
export function projectSignalen(rij = {}) {
  const lijst = [];
  const r = rij.ruimte;
  const sig = new Set(rij.signalen || []);
  (rij.deliverables || []).forEach((d) => (d.signalen || []).forEach((s) => sig.add(s)));
  const waarsch = rij.waarschuwingen || [];
  if ((r && r.past === false) || sig.has('past_niet')) {
    lijst.push({ code: 'past_niet', tekst: 'Past niet', ernst: 'probleem',
      uitleg: r ? `${uurHeel(r.open)} u open in ${r.werkdagen} werkdag${r.werkdagen === 1 ? '' : 'en'} tot de deadline.` : 'Meer open uren dan werkdagen tot de deadline.' });
  }
  if (sig.has('verlopen')) lijst.push({ code: 'verlopen', tekst: 'Deadline verlopen', ernst: 'probleem', uitleg: 'Een deadline is voorbij en de deliverable staat niet op afgerond.' });
  if (sig.has('voor_draaidag')) lijst.push({ code: 'voor_draaidag', tekst: 'Deadline vóór draaidag', ernst: 'probleem', uitleg: 'Een deadline valt eerder dan de laatste vaste draaidag.' });
  if (rij.staat === 'niets_gepland') lijst.push({ code: 'niets_gepland', tekst: 'Niets gepland', ernst: 'let_op', uitleg: 'Er zijn uren geoffreerd maar nog niets ingepland.' });
  if (!rij.eersteDeadline || sig.has('geen_deadline')) lijst.push({ code: 'geen_deadline', tekst: 'Geen deadline', ernst: 'let_op', uitleg: 'Er is montage of animatie geoffreerd, maar de deliverable heeft geen datum.' });
  if (waarsch.includes('verstreken_niet_afgerond')) {
    lijst.push({ code: 'verstreken_niet_afgerond', tekst: 'Verstreken taken niet afgevinkt', ernst: 'let_op',
      uitleg: `${rij.verstrekenAantal || ''} taken met een datum in het verleden staan niet op afgerond — waarschijnlijk gedaan. Ze tellen nu als gepland.`.trim() });
  }
  waarsch.filter((w) => w.startsWith('meer_gepland_')).forEach((w) => {
    const d = w.replace('meer_gepland_', '');
    const cijfers = rij.disciplines?.[d];
    lijst.push({ code: w, tekst: `Meer ${MEER_GEPLAND[d] || d} gepland dan geoffreerd`, ernst: 'info',
      uitleg: cijfers ? `${uurHeel(cijfers.geteld)} u gepland op ${uurHeel(cijfers.offerte)} u geoffreerd.` : '' });
  });
  if (rij.staat === 'geen_offerte_uren') lijst.push({ code: 'geen_offerte_uren', tekst: 'Geen offerte-uren', ernst: 'info', uitleg: 'Zonder offerte-uren weten we niet hoeveel er gepland moet worden.' });
  if (rij.staat === 'deels_gepland') lijst.push({ code: 'deels_gepland', tekst: 'Deels gepland', ernst: 'info', uitleg: 'Een deel van de geoffreerde uren staat al in de planning.' });
  if (r && r.past !== false && Number.isFinite(r.werkdagen)) {
    lijst.push({ code: 'ruimte', tekst: `${r.werkdagen} werkdagen voor ${uurHeel(r.open)} u`, ernst: 'info', uitleg: 'Ruimte tussen de laatste draaidag en de deadline.' });
  }
  const [hoofd = null, ...overige] = lijst;
  return { hoofd, overige };
}

/**
 * Uren per week per discipline voor de weektijdlijn. De uren van een taak worden
 * gelijk verdeeld over de weken die hij beslaat; wat buiten het venster valt telt als
 * eerder/later. Taken zonder datum komen apart.
 * → { [discipline]: { uren: number[], taken: Array<Array<taak>>, eerder, later, zonderDatum: {aantal, uren} } }
 */
export function urenPerWeek(taken, venster, disciplines) {
  const n = venster.weken.length;
  const uit = {};
  disciplines.forEach((d) => {
    uit[d] = { uren: Array(n).fill(0), taken: Array.from({ length: n }, () => []), eerder: 0, later: 0, zonderDatum: { aantal: 0, uren: 0 } };
  });
  taken.forEach((t) => {
    const rij = uit[t.discipline];
    if (!rij) return;
    const uren = Number(t.hours) || 0;
    const s = naarDatum(t.startDate) || naarDatum(t.endDate);
    if (!s) { rij.zonderDatum.aantal += 1; rij.zonderDatum.uren += uren; return; }
    const e = naarDatum(t.endDate) || s;
    const w0 = Math.round((maandagVan(s) - venster.start) / (7 * DAG_MS));
    const w1 = Math.max(w0, Math.round((maandagVan(e) - venster.start) / (7 * DAG_MS)));
    const deel = uren / (w1 - w0 + 1);
    for (let w = w0; w <= w1; w += 1) {
      if (w < 0) rij.eerder += deel;
      else if (w >= n) rij.later += deel;
      else { rij.uren[w] += deel; if (!rij.taken[w].includes(t)) rij.taken[w].push(t); }
    }
  });
  return uit;
}

/** Plek van een datum in het venster: weekindex + fractie binnen de week (0..1), of null. */
export function datumInVenster(iso, venster) {
  const d = naarDatum(iso);
  if (!d) return null;
  const w = Math.round((maandagVan(d) - venster.start) / (7 * DAG_MS));
  if (w < 0 || w >= venster.weken.length) return null;
  const dag = (d.getDay() + 6) % 7; // ma = 0
  return { kolom: w, fractie: Math.min(1, (dag + 0.5) / 5) };
}

const DISCIPLINE_NAAM = { montage: 'montage', animatie: 'animatie', feedback: 'feedback' };

/**
 * Offerte-uren die nog geen taak zijn: per discipline het "te plannen"-deel (open),
 * alleen waar geoffreerd is en afgerond op hele uren > 0. Voor de lege bak op het
 * planbord: "geen taken in de bak" betekent niet "alles gepland" als er nog uren
 * zonder taak zijn (Joris, 25 sep: 7584, 96 u montage).
 * @returns {Array<{ key: string, naam: string, uren: number }>}
 */
export function nogGeenTaak(rij = {}, disciplines = ['montage', 'animatie', 'feedback']) {
  return disciplines
    .map((key) => {
      const s = samenvatDiscipline(rij.disciplines?.[key]);
      return { key, naam: DISCIPLINE_NAAM[key] || key, uren: s.offerte ? Math.round(s.open) : 0 };
    })
    .filter((x) => x.uren > 0);
}

/** "96 u montage en 13 u animatie zijn nog geen taak" (leeg bij niets). */
export function nogGeenTaakTekst(lijst = []) {
  if (!lijst.length) return '';
  const delen = lijst.map((x) => `${x.uren} u ${x.naam}`);
  const opsomming = delen.length > 1 ? `${delen.slice(0, -1).join(', ')} en ${delen[delen.length - 1]}` : delen[0];
  const enkel = lijst.length === 1 && lijst[0].uren === 1;
  return `${opsomming} ${enkel ? 'is' : 'zijn'} nog geen taak`;
}
